import { zodResolver } from '@hookform/resolvers/zod';
import { CalendarIcon, Eye } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import BreadCrumb from '../../breadcrumb';
import Header from '../../DashboardNavigation/header';
import Sidebar from '../../DashboardNavigation/sidebar';
import { Button } from '../../ui/button';
import { Checkbox } from '../../ui/checkbox';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../../ui/form';
import { Heading } from '../../ui/heading';
import { Input } from '../../ui/input';
import { Separator } from '../../ui/separator';
import { useToast } from '../../ui/use-toast';
import { LoadingOverlay } from '../../ui/loading-overlay';
import {
  NewsFormValues,
  newsSchema,
  updatedNewsSchema,
  UpdatedNewsSchema,
} from './news-zod.schema';

import QuillEditor from '../../../lib/quill/quill';
import { cn } from '../../../lib/utils';
import FileUpload from '../../FileUpload/FileUpload';
import { Calendar } from '../../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { getNewsById, handleCreateNews, handleUpdateNews } from '../../../API/News/newsService';

export const NewsForm: React.ComponentType<any> = () => {
  const uploadUrl = process.env.REACT_APP_FILE_UPLOAD;

  const router = useNavigate();
  const { id } = useParams();
  const initialData = id ? parseInt(id) : undefined;
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const pageTitle = initialData ? 'Modifier actualité' : 'Ajouter de nouvelles actualités';
  const pageDescription = initialData ? "Modifier l'actualité" : '';
  const actionLabel = initialData ? 'Modifier' : 'Ajouter';

  const form = useForm<NewsFormValues | UpdatedNewsSchema>({
    resolver: zodResolver(initialData ? updatedNewsSchema : newsSchema),
    defaultValues: {
      title: '',
      date: undefined,
      description: '',
      image: [],
      isDraft: false,
    },
    mode: 'onChange',
  });

  const isDraftValue = form.watch('isDraft') as boolean | undefined;

  const onSubmit = async () => {
    setLoading(true);
    const formData = form.getValues();
    const isDraft = !!(formData as any).isDraft;
    try {
      if (initialData) {
        await handleUpdateNews(initialData, formData, router, isDraft);
      } else {
        await handleCreateNews(formData, router, isDraft);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const [data, setData] = useState<any>();

  useEffect(() => {
    const fetchData = async () => {
      if (initialData) {
        const newsData = await getNewsById(initialData);
        setData(newsData);
        form.reset({
          title: newsData.title,
          description: newsData.description,
          date: newsData.date ? new Date(newsData.date) : undefined,
          isDraft: newsData.isDraft === true,
          image: newsData.image
            ? [
                {
                  fileName: newsData.image,
                  name: newsData.image,
                  fileSize: 0,
                  size: 0,
                  key: newsData.image,
                },
              ]
            : [],
        });
      }
    };

    if (initialData) {
      fetchData();
    }
  }, [initialData]);

  const breadcrumbItems = [
    { title: 'Actualités', link: '/admin-dashboard/actualités' },
    {
      title: initialData ? 'Mise à jour' : 'Ajouter',
      link: '/admin-dashboard/actualité/ajouter',
    },
  ];

  const previewValues = form.watch();

  return (
    <>
      <Header />
      <div className="flex h-screen">
        <Sidebar />
        <div className="w-full p-10 overflow-y-auto">
          <BreadCrumb items={breadcrumbItems} />
          <div className="flex items-center justify-between">
            <Heading title={pageTitle} description={pageDescription} />
          </div>
          <Separator />

          <LoadingOverlay isVisible={loading} />
          <Form {...form}>
            <form
              className="w-full space-y-8 mt-6"
              onSubmit={form.handleSubmit(onSubmit)}
              encType="multipart/form-data"
            >
              {/* Titre + Date */}
              <div className="flex justify-between gap-6">
                <div className="w-1/2">
                  <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Titre</FormLabel>
                        <FormControl>
                          <Input disabled={loading} placeholder="Titre..." {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <div>
                  <FormField
                    control={form.control}
                    name="date"
                    render={({ field }) => (
                      <FormItem className="flex flex-col mt-3">
                        <FormLabel>Date de publication</FormLabel>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant={'outline'}
                              className={cn(
                                'w-[280px] justify-start text-left font-normal',
                                !field.value && 'text-muted-foreground'
                              )}
                            >
                              <CalendarIcon className="w-4 h-4 mr-2" />
                              {field.value ? (
                                (field.value as Date).toLocaleDateString('fr-FR')
                              ) : (
                                <span>Choisir une date</span>
                              )}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0">
                            <Calendar
                              mode="single"
                              selected={field.value as Date}
                              onSelect={date => field.onChange(date)}
                            />
                          </PopoverContent>
                        </Popover>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              {/* Description */}
              <div className="w-full">
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description</FormLabel>
                      <FormControl>
                        <QuillEditor
                          value={(field.value as string) || ''}
                          onChange={(value: string) => {
                            field.onChange(value);
                            form.trigger('description');
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Image actuelle (mode édition) */}
              {data?.image && (
                <div className="mt-2 mb-2">
                  <p className="text-sm text-muted-foreground mb-1">Image actuelle :</p>
                  <img
                    src={`${uploadUrl}/news/${data.image}`}
                    alt="Image actuelle"
                    className="h-24 w-auto rounded border"
                  />
                </div>
              )}

              {/* Upload image */}
              <FormField
                control={form.control}
                name="image"
                render={() => (
                  <FormItem>
                    <FormLabel>Image </FormLabel>
                    <FormControl>
                      <FileUpload
                        onChange={files => {
                          const mappedFiles = files.map(file => ({
                            key: file.id,
                            fileName: file.fileName,
                            name: file.fileName,
                            fileSize: parseInt(file.fileSize),
                            size: parseInt(file.fileSize),
                            file: file.file,
                          }));
                          form.setValue('image', mappedFiles);
                          // Re-valider l'image quand isDraft change
                          form.trigger('image');
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Checkbox brouillon */}
              <FormField
                control={form.control}
                name="isDraft"
                render={({ field }) => (
                  <FormItem className="flex items-center gap-3 mt-2">
                    <FormControl>
                      <Checkbox
                        id="isDraft"
                        checked={!!field.value}
                        onCheckedChange={checked => {
                          field.onChange(!!checked);
                          if (checked) {
                            // Brouillon coché → l'image n'est plus requise, effacer l'erreur
                            form.clearErrors('image');
                          } else {
                            // Brouillon décoché → re-valider l'image
                            form.trigger('image');
                          }
                        }}
                        disabled={loading}
                      />
                    </FormControl>
                    <FormLabel
                      htmlFor="isDraft"
                      className="cursor-pointer !mt-0 font-normal text-sm"
                    >
                      Sauvegarder en brouillon{' '}
                      <span className="text-muted-foreground">(non visible publiquement)</span>
                    </FormLabel>
                  </FormItem>
                )}
              />

              {/* Boutons d'action */}
              <div className="flex items-center gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  disabled={loading}
                  onClick={() => router(-1)}
                >
                  Annuler
                </Button>

                <Button disabled={loading} isLoading={loading} type="submit">
                  {actionLabel}
                </Button>

                <Button
                  disabled={loading}
                  type="button"
                  variant="outline"
                  className="border-blue-500 text-blue-600 hover:bg-blue-50"
                  onClick={() => setPreviewOpen(true)}
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Aperçu avant publication
                </Button>
              </div>
            </form>
          </Form>
        </div>
      </div>

      {/* Modal aperçu */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Aperçu avant publication</DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            {/* Badge brouillon */}
            {(previewValues as any).isDraft && (
              <div className="mb-4 p-2 bg-yellow-100 border border-yellow-400 rounded text-yellow-800 text-sm">
                ⚠️ Cette actualité est en <strong>brouillon</strong> — elle ne sera pas visible
                publiquement après enregistrement.
              </div>
            )}

            <h1 className="text-2xl font-bold text-black">
              {(previewValues as any).title || "Titre de l'actualité"}
            </h1>

            {(previewValues as any).date && (
              <p className="text-gray-500 mt-1">
                {((previewValues as any).date as Date).toLocaleDateString('fr-FR', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </p>
            )}
            <hr className="w-full h-1 mt-2 bg-primary" />

            {/* Image aperçu */}
            {previewValues.image && (previewValues.image as any[]).length > 0 && (
              <div className="mt-4">
                {(previewValues.image as any[])[0]?.file instanceof File ? (
                  <img
                    src={URL.createObjectURL((previewValues.image as any[])[0].file)}
                    alt="Aperçu"
                    className="w-full rounded-md"
                  />
                ) : data?.image ? (
                  <img
                    src={`${uploadUrl}/news/${data.image}`}
                    alt="Aperçu"
                    className="w-full rounded-md"
                  />
                ) : null}
              </div>
            )}

            {/* Description HTML */}
            {(previewValues as any).description && (
              <div
                className="mt-6 prose max-w-none"
                dangerouslySetInnerHTML={{
                  __html: (previewValues as any).description as string,
                }}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
